import type { CSSProperties, SVGProps } from 'react';
import cn from 'classnames';
import styles from './Icon.module.scss';
import { iconRegistry, type IconPaint } from './iconRegistry';
import type { IconColor, IconName } from './types';

type IconCssVars = CSSProperties & {
  '--icon-color'?: string;
};

const colorValueByKey: Record<IconColor, string> = {
  currentColor: 'currentColor',
  primary: 'var(--icon-accent)',
  primaryDisabled: 'var(--action-primary-disabled-alt)',
  black: 'var(--color-gray-900)',
  white: 'var(--color-white)',
  blue: 'var(--color-blue-500)',
  lightBlue: 'var(--chart-2)',
  orange: 'var(--chart-3)',
  violet: 'var(--chart-4)',
  yellow: 'var(--chart-5)',
  pink: 'var(--chart-6)',
  green: 'var(--chart-7)',
  gray: 'var(--color-blue-150)',
  bordo: 'var(--color-danger-deep)',
  red: 'var(--color-red-100)',
};

const paintClassByMode: Record<IconPaint, string | undefined> = {
  stroke: styles.iconStroke,
  fill: styles.iconFill,
  both: styles.iconBoth,
  fixed: undefined,
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'color'> {
  name: IconName;
  color?: IconColor;
}

export const Icon = ({ name, color = 'currentColor', className, style, ...rest }: IconProps) => {
  const iconEntry = iconRegistry[name];
  const IconComponent = iconEntry.component;

  if (iconEntry.paint === 'fixed') {
    return <IconComponent className={className} style={style} {...rest} />;
  }

  const mergedStyle: IconCssVars = {
    ...(style as IconCssVars),
    '--icon-color': colorValueByKey[color],
  };

  return (
    <IconComponent
      className={cn(paintClassByMode[iconEntry.paint], className)}
      style={mergedStyle}
      {...rest}
    />
  );
};
